// ============================================================================
// u4 — Sales & documents: customer hub, lead conversion, quote lifecycle,
// contracts, document classification/links/signatures, portal quotes.
// Shapes follow the u4 backend API contract.
// ============================================================================
import type {
  EngagementType,
  Lead,
  Obligation,
  QuoteStatus,
  QuoteType,
} from './index';

// --- Customers (organizations) ---------------------------------------------

/** P-01/B2 — one engagement of a "סוגי עבודה" tag (active / on hold). */
export interface CustomerWorkTypeEngagement {
  id: string;
  name: string | null;
  status: 'active' | 'on_hold';
  /** A 'project' engagement's projects row (its project page). */
  project_id: string | null;
}

/** P-01/B2 — the open engagements of one type for a customer. */
export interface CustomerWorkType {
  type: EngagementType;
  count: number;
  engagements: CustomerWorkTypeEngagement[];
  /** hour_bank only: available balance of the open banks. */
  hour_bank_balance?: number;
}

export interface OrganizationDeleteImpact {
  organization: { id: string; name: string; active: boolean };
  counts: Record<string, number>;
  total: number;
  /** Non-empty ⇒ hard delete is refused by the server (e.g. `employee_profiles`). */
  blocking: string[];
}

/**
 * Pre-delete warning for an EMPLOYEE. Mirrors `EmployeeDeleteImpact` on the
 * server: `counts` is per-table, keyed identically to the customer impact, and
 * `total` is their sum. `blocking` is non-empty when work hours exist (T-03 / D-25:
 * hours are never deleted) — the screen then disables the delete.
 */
export interface EmployeeDeleteImpact {
  employee: { id: string; user_id: string | null; name: string | null };
  counts: Record<string, number>;
  total: number;
  /** T-03 — 'has_work_hours' | 'customer_tickets_have_hours'; non-empty ⇒ deactivate only. */
  blocking: string[];
  /** T-03 — false when `blocking` is non-empty. */
  can_hard_delete?: boolean;
  /** T-03 — the server's refusal message for the first blocking reason. */
  blocking_message?: string | null;
}

export interface CustomerHubPermissions {
  can_write: boolean;
  is_general_manager: boolean;
  can_view_billing: boolean;
  can_view_profitability: boolean;
}

export interface CustomerHubSummary {
  organization: { id: string; name: string; active: boolean };
  permissions: CustomerHubPermissions;
  counts: {
    open_quotes: number;
    accepted_quotes: number;
    accepted_quotes_without_contract: number;
    draft_contracts: number;
    active_contracts: number;
    documents: number;
    open_service_requests: number;
    /** P-01/C3 — tasks (task_type 'task') not completed and not cancelled. */
    open_tasks: number;
    engagements: number;
    leads_converted: number;
  };
  /** P-01/C3 — open engagements by type (hour-bank balance included). */
  work_types: CustomerWorkType[];
  /** P-01/C3 — this month's usage of each ACTIVE service contract. */
  service_contract_usage: {
    engagement_id: string; name: string | null; consumed_hours: number; quota: number | null;
    /** FU-14 — the monthly quota without the approved extra hours, and those hours. */
    base_quota?: number | null; extra_hours?: number;
  }[];
  accepted_quotes_without_contract: {
    id: string;
    quote_number: number;
    revision_number: number;
    title: string;
    accepted_at: string | null;
  }[];
  /**
   * P-07 (D-74 §5, §6) — the active "אפיון" cards; `in_characterization` = an
   * active card and no open engagement yet ("באפיון").
   */
  characterization?: { active: { id: string; title: string }[]; in_characterization: boolean };
}

// ---------------------------------------------------------------------------
// P-07 (D-49, D-50, D-74) — the "אפיון" card
// ---------------------------------------------------------------------------
export type CharacterizationStatus = 'active' | 'completed' | 'cancelled';

export interface CharacterizationPayment {
  installment_index: 1 | 2;
  percent: number;
  /** Before VAT (D-74 §4). Hidden in a banner for who may not see the money. */
  amount?: number | null;
  status: 'pending' | 'paid';
  paid_at: string | null;
  paid_by?: string | null;
  paid_by_name?: string | null;
  note?: string | null;
  /** D-143 — the approval stage of the installment's charge (null: no live charge). */
  charge_approval_status?: string | null;
  /** D-262 (QX-1243): "שולם" was cancelled but the charge is still locked - the manager may release it by hand. */
  charge_locked?: boolean;
}

export interface Characterization {
  id: string;
  organization_id: string;
  lead_id: string | null;
  quote_id: string | null;
  quote_number?: number | null;
  quote_title?: string | null;
  title: string;
  status: CharacterizationStatus;
  /** Before VAT (D-74 §4). */
  price: number | null;
  notes: string | null;
  task_id: string | null;
  task_status?: string | null;
  minutes: number;
  hours: number;
  payments: CharacterizationPayment[];
  engagement: { id: string; type: EngagementType; name: string | null; project_id: string | null } | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
  /** P-51 (D-224 s.1): the stage bar from the conversion on (the card's meeting and the build quote).
   *  FIX-B28 (QX-522): null when the card did not come from a lead. */
  journey?: {
    characterization_meeting: { id: string; title?: string | null; meeting_datetime: string; status: string; meeting_type?: string | null } | null;
    build_quote: { id: string; quote_number?: number | string; title?: string | null; status: string; quote_type?: string; signed_at?: string | null } | null;
  } | null;
}

/** The card as a banner inside the engagement opened from it (D-74 §8). */
export interface CharacterizationBanner {
  id: string;
  organization_id: string;
  title: string;
  status: CharacterizationStatus;
  minutes: number;
  hours: number;
  completed_at: string | null;
  price?: number | null;
  payments: CharacterizationPayment[];
}

/** D-321 - GET /customer-hub/internal/overview (no money; `month_cost` only when `can_view_cost`). */
export interface InternalHubOverview {
  organization: { id: string; name: string };
  open_tasks: number;
  month_minutes: number;
  active_projects: number;
  can_view_cost: boolean;
  month_cost: number | null;
}

export interface CustomerWorkSummary {
  total_minutes: number;
  billable_minutes: number;
  non_billable_minutes: number;
  by_month: { month: string; minutes: number; billable_minutes: number }[];
  /** P-07 — a characterization task's hours are their own line (characterization_id / _title). */
  by_project: {
    project_id: string | null;
    project_name: string | null;
    characterization_id?: string | null;
    characterization_title?: string | null;
    minutes: number;
  }[];
  recent: {
    id: string;
    start_time: string;
    agent_name: string | null;
    duration_minutes: number;
    description: string | null;
    billable: boolean;
    ticket_id: string | null;
    ticket_title: string | null;
  }[];
}

export interface OrganizationBilling {
  organization_id: string;
  obligations: { rows: Obligation[]; total: number };
  balance: number;
  in_debt: boolean;
}

// --- Lead conversion ---------------------------------------------------------

export type CustomerMatchReason = 'tax_id' | 'email' | 'phone' | 'name_exact';
export type ContactMatchReason = 'email' | 'phone';

export interface ProposedCustomer {
  name: string;
  tax_id: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  /** P-01/F2 — the lead's notes travel to the new customer. */
  notes?: string | null;
}

export interface CustomerCandidate {
  id: string;
  name: string;
  tax_id: string | null;
  email: string | null;
  phone: string | null;
  active: boolean;
  /** QX-646: the linked customer's Account Manager (may grant portal access in the conversion). */
  account_manager_id?: string | null;
  match_reasons: CustomerMatchReason[];
  similarity: number;
}

export interface ContactCandidate {
  id: string;
  organization_id: string;
  organization_name: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  match_reasons: ContactMatchReason[];
}

export interface CommitmentQuote {
  id: string;
  quote_number: number;
  revision_number: number;
  title: string;
  status: QuoteStatus;
  quote_type: QuoteType;
  accepted_at: string | null;
  signed_at: string | null;
  grand_total: number;
}

export interface EngagementProposal {
  type: EngagementType;
  name: string;
  quote_id: string;
}

export interface LeadConversionPreview {
  lead: Lead;
  already_converted: boolean;
  proposed_customer: ProposedCustomer;
  /** P-01/F1 — the lead's owner when they are an active OTE employee, else null. */
  default_account_manager_id?: string | null;
  proposed_contact: { name: string | null; email: string | null; phone: string | null } | null;
  customer_candidates: CustomerCandidate[];
  contact_candidates: ContactCandidate[];
  commitment: {
    satisfied: boolean;
    quotes: CommitmentQuote[];
    selected_quote_id: string | null;
  };
  engagement_proposal: EngagementProposal | null;
  missing_fields: string[];
  can_override: boolean;
}

export interface LeadConvertPayload {
  quote_id?: string;
  customer:
    | { link_to_org_id: string }
    | {
        create: {
          name: string;
          tax_id?: string;
          email?: string;
          phone?: string;
          address?: string;
          city?: string;
          website?: string;
          notes?: string;
          /** P-01/F1 — required by the server for a new customer. */
          account_manager_id?: string;
        };
      };
  contact?: { link_contact_id: string } | { create: { name: string; email?: string; phone?: string; access?: ConvertContactAccess } } | { skip: true };
  /** D-230 s.10 - additional contacts, each optionally with portal access. */
  extra_contacts?: Array<{ name: string; email?: string; phone?: string; access?: ConvertContactAccess }>;
  engagement?: { type: EngagementType; name: string } | null;
  override_reason?: string;
}

/** D-230 s.10 - "ליצור גישה לפורטל" for one contact of the conversion. */
export interface ConvertContactAccess {
  access_type: 'customer_manager' | 'primary_contact' | 'customer_employee';
  password: string;
  approve_tickets?: boolean;
  /** The login e-mail when it differs from the contact's own. */
  email?: string;
}

/** D-230 s.11 - GET /leads/:id/conversion-email-check. */
export interface ConversionEmailHolder {
  kind: 'user' | 'contact' | 'lead';
  id: string;
  name: string | null;
  organization_id?: string | null;
  organization_name?: string | null;
  href: string | null;
}
export interface ConversionEmailCheck {
  email: string;
  taken: boolean;
  holders: ConversionEmailHolder[];
  hidden_holder: boolean;
}

export interface LeadConvertResult {
  /** D-331 round 3: a new customer was opened with no Account Manager (the converter is not the OTE manager). */
  needs_account_manager?: boolean;
  lead_id: string;
  organization_id: string;
  contact_id: string | null;
  engagement_id: string | null;
  quote_id: string | null;
  /** D-138 / D-141 — the portal contact created with the new customer. */
  portal_access?: { contact_id: string; user_id: string; email: string };
  email_sent?: boolean;
  /** D-230 s.10 - portal access created for the conversion's contacts. */
  contact_access?: Array<{ contact_id: string; user_id: string; email: string; email_sent?: boolean }>;
}

// --- Quotes ------------------------------------------------------------------

export type QuoteTransitionAction = 'send' | 'mark_viewed' | 'accept' | 'reject' | 'cancel' | 'expire';

export interface QuoteEvent {
  id: string;
  event_type: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_type: string | null;
  at: string;
  metadata: Record<string, unknown> | null;
}

export interface QuoteRevisionRef {
  id: string;
  revision_number: number;
  status: QuoteStatus;
  created_at: string;
}

export interface QuoteLifecycle {
  accepted_at: string | null;
  signed_at: string | null;
  billed: { count: number; amount: number };
  paid: { count: number; amount: number };
}

export interface QuoteConversionOutcome {
  status: 'converted' | 'needs_review' | 'not_applicable';
  organization_id?: string;
}

// --- Portal quotes -------------------------------------------------------------

export interface PortalQuote {
  id: string;
  quote_number: number;
  revision_number: number;
  title: string;
  status: QuoteStatus;
  quote_type: QuoteType;
  grand_total: number;
  total_amount: number;
  vat_total: number;
  valid_until: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  organization_id: string;
}

export interface PortalQuoteDetail extends PortalQuote {
  /** DQ-095 (D-312 s.3): the monthly server charge of a project / development quote - NOT inside grand_total. */
  server_cost?: number | string | null;
  line_items: { description: string; quantity: number; unit_price: number; total: number; vat_amount: number }[];
}

// --- Contracts -----------------------------------------------------------------

export type ContractStatus = 'draft' | 'pending_signature' | 'signed' | 'active' | 'expired' | 'terminated';

export interface ContractParty {
  role: string;
  name: string;
  organization_id?: string | null;
  contact_id?: string | null;
}

export interface Contract {
  id: string;
  organization_id: string;
  organization_name: string | null;
  quote_id: string | null;
  quote_number: number | null;
  engagement_id: string | null;
  title: string;
  terms: string | null;
  start_date: string | null;
  end_date: string | null;
  parties: ContractParty[];
  status: ContractStatus;
  current_version: number;
  signed_at: string | null;
  terminated_at: string | null;
  status_reason: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContractVersion {
  id: string;
  version_number: number;
  title: string;
  terms: string | null;
  start_date: string | null;
  end_date: string | null;
  parties: ContractParty[];
  change_note: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface ContractDetail extends Contract {
  versions: ContractVersion[];
  documents: { id: string; title: string; status: string; classification: string }[];
}

export interface ContractInput {
  title?: string;
  terms?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  parties?: ContractParty[];
  engagement_id?: string | null;
  change_note?: string;
}

/** Allowed contract transitions (mirrors the server state machine). */
export const CONTRACT_TRANSITIONS: Record<ContractStatus, ContractStatus[]> = {
  draft: ['pending_signature'],
  pending_signature: ['draft', 'signed'],
  signed: ['active', 'terminated'],
  active: ['expired', 'terminated'],
  expired: [],
  terminated: [],
};

// --- Documents: links + signatures ---------------------------------------------

export type DocumentLinkEntityType = 'project' | 'quote' | 'contract' | 'ticket' | 'engagement' | 'lead' | 'characterization';

export interface DocumentLink {
  id: string;
  entity_type: DocumentLinkEntityType;
  entity_id: string;
  entity_label: string | null;
  created_at: string;
}

export interface DocumentSignature {
  id: string;
  version_id: string;
  signer_name: string | null;
  signer_email: string | null;
  provider: string | null;
  provider_ref: string | null;
  status: string;
  signed_at: string | null;
  content_hash: string | null;
}
